import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, ArrowUp, ArrowUpRight, AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { LeadModal } from "./LeadModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GREETING =
  "Soy Angelo, fundador de Servin Labs. En 2026, si un negocio no tiene un ecosistema y presencia digital, está regalándole su mercado a la competencia. Nosotros no hacemos páginas; construimos imperios. ¿Qué construimos para ti?";

const ERROR_MSG =
  "La conexión falló por un momento. Inténtalo de nuevo — o escríbeme directo: angelobusinessai@gmail.com";

// Strip the invisible [BRIEF] intent token (and trailing partials while streaming)
const displayText = (t) =>
  t.replace(/\[BRIEF\]/g, "").replace(/\[B?R?I?E?F?\]?\s*$/, "").trim();

const getSessionId = () => {
  let id = localStorage.getItem("angelo_session_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("angelo_session_id", id);
  }
  return id;
};

const Bubble = ({ msg }) => {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="chat-message-user">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-white px-4 py-3 text-sm leading-relaxed text-black">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2.5" data-testid="chat-message-assistant">
      <img
        src="/assets/servin-labs-logo.png"
        alt=""
        loading="lazy"
        className="mb-1 h-6 w-6 shrink-0 object-contain"
      />
      <div
        className={`max-w-[85%] rounded-2xl rounded-bl-md border px-4 py-3 text-sm leading-relaxed ${
          msg.error
            ? "border-white/25 bg-white/[0.07] text-white/80"
            : "border-white/10 bg-white/5 text-white/90"
        }`}
      >
        {msg.error && (
          <AlertCircle className="mb-1 h-3.5 w-3.5 text-white/70" aria-hidden="true" />
        )}
        {displayText(msg.content)}
      </div>
    </div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [briefSuggested, setBriefSuggested] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [pendingAsk, setPendingAsk] = useState(null);
  const [retryText, setRetryText] = useState(null);
  const [sessionId] = useState(() => getSessionId());
  const scrollRef = useRef(null);

  const userMsgCount = messages.filter((m) => m.role === "user").length;

  const openBrief = async () => {
    if (briefLoading) return;
    setBriefLoading(true);
    try {
      const res = await axios.post(`${API}/chat/${sessionId}/brief-summary`);
      setPrefill({ name: res.data.name || "", brief: res.data.brief || "" });
    } catch {
      setPrefill({ name: "", brief: "" });
      toast.error("No pude preparar tu brief automáticamente — puedes escribirlo directo.");
    }
    setBriefLoading(false);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!open || historyLoaded) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/chat/${sessionId}`);
        const msgs = res.data.messages || [];
        const hist = msgs.length
          ? msgs.map((m) => ({ role: m.role, content: m.content }))
          : [{ role: "assistant", content: GREETING }];
        // Merge instead of overwrite: a stream may already be in flight
        // (e.g. CTA auto-send or a fast user) — never clobber it.
        setMessages((cur) => (cur.length ? [...hist, ...cur] : hist));
      } catch {
        setMessages((cur) =>
          cur.length ? cur : [{ role: "assistant", content: GREETING }]
        );
      }
      setHistoryLoaded(true);
    })();
  }, [open, historyLoaded, sessionId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming, open]);

  const sendText = async (raw, opts = {}) => {
    const text = (raw || "").trim();
    if (!text || streaming) return;
    setStreaming(true);
    setRetryText(null);
    setMessages((m) => {
      let base = m;
      // On retry, drop the previous error/interrupted bubble before streaming again.
      if (opts.retry && base.length) {
        const last = base[base.length - 1];
        if (last.role === "assistant" && (last.error || last.interrupted)) {
          base = base.slice(0, -1);
        }
      }
      return opts.retry
        ? [...base, { role: "assistant", content: "" }]
        : [...base, { role: "user", content: text }, { role: "assistant", content: "" }];
    });

    const patchLast = (fn) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = fn(copy[copy.length - 1]);
        return copy;
      });

    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad_response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let serverError = false;
      let gotDone = false;
      const handleBlock = (block) => {
        // SSE spec: an event may span multiple "data:" lines; comment lines
        // (keep-alives, ": ping") have no data lines and are skipped.
        const dataLines = block.split("\n").filter((l) => l.startsWith("data:"));
        if (!dataLines.length) return;
        const payload = dataLines.map((l) => l.slice(5).trimStart()).join("\n");
        let ev;
        try {
          ev = JSON.parse(payload);
        } catch {
          return; // one malformed event must never abort the whole stream
        }
        if (ev.type === "delta" && ev.content) {
          acc += ev.content;
          patchLast((last) => ({ ...last, content: last.content + ev.content }));
        } else if (ev.type === "done") {
          gotDone = true;
        } else if (ev.type === "error") {
          serverError = true;
        }
      };
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        // Buffer across chunk boundaries; normalize CRLF from proxies.
        buf += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
        const blocks = buf.split("\n\n");
        buf = blocks.pop();
        blocks.forEach(handleBlock);
      }
      if (buf.trim()) handleBlock(buf);

      if (acc.includes("[BRIEF]")) setBriefSuggested(true);
      if (!acc) {
        // Nothing streamed: server error, empty reply, or connection cut early.
        patchLast(() => ({ role: "assistant", content: ERROR_MSG, error: true }));
        setRetryText(text);
      } else if (serverError || !gotDone) {
        // Partial reply then failure: keep the text, flag it, offer retry.
        patchLast((last) => ({ ...last, interrupted: true }));
        setRetryText(text);
      }
    } catch {
      // Genuine network failure — never wipe partial text already streamed.
      patchLast((last) =>
        last.content
          ? { ...last, interrupted: true }
          : { role: "assistant", content: ERROR_MSG, error: true }
      );
      setRetryText(text);
    } finally {
      setStreaming(false);
    }
  };

  const send = (e) => {
    e?.preventDefault();
    const text = input;
    setInput("");
    sendText(text);
  };

  // External ask events (e.g. NFC "Personaliza tu NFC ahora" CTA)
  useEffect(() => {
    const onAsk = (e) => {
      setPendingAsk(e.detail?.message || null);
      setOpen(true);
    };
    window.addEventListener("angelo:ask", onAsk);
    return () => window.removeEventListener("angelo:ask", onAsk);
  }, []);

  useEffect(() => {
    if (open && historyLoaded && pendingAsk && !streaming) {
      const msg = pendingAsk;
      setPendingAsk(null);
      sendText(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, historyLoaded, pendingAsk, streaming]);

  const lastMsg = messages[messages.length - 1];
  const showTyping = streaming && lastMsg && lastMsg.role === "assistant" && !lastMsg.content;

  return (
    <>
      <motion.button
        type="button"
        data-testid="chat-toggle-button"
        aria-label="Abrir chat con Angelo AI"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.07 }}
        whileTap={{ scale: 0.93 }}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-white/5 shadow-[0_0_22px_-6px_rgba(255,255,255,0.5)] backdrop-blur-xl md:bottom-8 md:right-8"
      >
        {open ? (
          <X className="h-6 w-6 text-white" aria-hidden="true" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" aria-hidden="true" />
        )}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-pulse rounded-full bg-white/80" aria-hidden="true" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-black/85 backdrop-blur-2xl md:inset-x-auto md:bottom-28 md:right-8 md:top-auto md:h-[600px] md:max-h-[calc(100vh-160px)] md:w-[400px] md:rounded-3xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <img
                src="/assets/servin-labs-logo.png"
                alt="Angelo AI"
                className="h-9 w-9 object-contain"
              />
              <div className="flex-1">
                <p className="font-display text-sm font-semibold tracking-wide text-white">
                  Angelo AI
                </p>
                <p className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] text-white/40">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/70" />
                  FUNDADOR DIGITAL — SERVIN LABS
                </p>
              </div>
              <button
                type="button"
                data-testid="chat-close-button"
                aria-label="Cerrar chat"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
            >
              {messages.map((m, i) =>
                i === messages.length - 1 && showTyping ? null : <Bubble key={i} msg={m} />
              )}
              {showTyping && (
                <div className="flex items-end gap-2.5" data-testid="typing-indicator">
                  <img
                    src="/assets/servin-labs-logo.png"
                    alt=""
                    className="mb-1 h-6 w-6 shrink-0 object-contain"
                  />
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-white/10 bg-white/5 px-4 py-4">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
              {retryText && !streaming && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="chat-retry-button"
                  onClick={() => sendText(retryText, { retry: true })}
                  className="ml-9 flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Reintentar
                </motion.button>
              )}
              {briefSuggested && !streaming && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="chat-brief-button"
                  onClick={openBrief}
                  disabled={briefLoading}
                  className="ml-9 flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-black disabled:opacity-50"
                >
                  {briefLoading ? "Preparando tu brief..." : "Enviar mi brief"}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </motion.button>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/10">
              <form onSubmit={send} className="flex items-center gap-3 px-4 pt-4 pb-4">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escríbele a Angelo..."
                  data-testid="chat-input"
                  className="h-12 flex-1 rounded-full border border-white/10 bg-white/5 px-5 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="chat-send-button"
                  aria-label="Enviar mensaje"
                  disabled={streaming || !input.trim()}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-black transition-opacity duration-300 hover:opacity-85 disabled:opacity-35"
                >
                  <ArrowUp className="h-5 w-5" aria-hidden="true" />
                </button>
              </form>
              {userMsgCount > 0 && !briefSuggested && (
                <button
                  type="button"
                  data-testid="chat-brief-fallback"
                  onClick={openBrief}
                  disabled={briefLoading}
                  className="-mt-2 w-full pb-3 text-center text-[11px] tracking-wide text-white/35 transition-colors duration-300 hover:text-white disabled:opacity-50"
                >
                  {briefLoading ? "Preparando tu brief..." : "Enviar mi brief →"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        prefill={prefill}
        source="chat"
      />
    </>
  );
};
